import axios from 'axios'
import { AuthorizedApi } from './index.js'
import { requestWithUserAuth } from './auth.js'
import store from '../state/store.js'
import { serverFavoritesLoaded } from '../state/events'

/**
 * GET /api/v1/user/favorites
 *
 * Uses axios directly (not AuthorizedApi) so that an expected 404 does not
 * trigger the global "Disconnected" overlay that AuthorizedApi always fires
 * on any error. Token refresh is still handled via requestWithUserAuth.
 *
 * @param {object}   config      - App config (publicBackendUrl, apiUri)
 * @param {object}   auth        - Auth state
 * @param {number}   [year]      - Convention year for localStorage keying.
 * @param {Function} [onLoaded]  - Optional callback invoked with the merged
 *                                 ID array after state is updated.
 */
export function fetchFavorites (config, auth, year, onLoaded) {
  requestWithUserAuth(config, auth, (freshAuth) => {
    const { publicBackendUrl, apiUri } = config
    axios
      .get(`${publicBackendUrl}/${apiUri}/user/favorites`, {
        headers: { Authorization: freshAuth?.accessToken }
      })
      .then((response) => {
        const ids = response.data?.favorite_event_ids ?? []
        store.dispatch(serverFavoritesLoaded({ ids, year }))
        if (onLoaded) {
          const mergedIds = store.getState().schedule?.favorite_ids ?? []
          onLoaded(mergedIds)
        }
      })
      .catch((error) => {
        // 404 = no server record yet. If we have local favorites, push them
        // up to initialise the server record.
        if (error?.response?.status === 404) {
          const localIds = store.getState().schedule?.favorite_ids ?? []
          if (localIds.length > 0) {
            pushFavorites(config, auth, localIds)
          }
        }
        // All other errors: silently ignore — local favorites remain in effect.
      })
  })
}

/**
 * PUT /api/v1/user/favorites
 *
 * Pushes the complete list of favorite event IDs to the server.
 * Uses requestWithUserAuth so expired tokens are refreshed automatically.
 * Errors are swallowed — local state is already updated; the server is
 * treated as an eventual-consistency replica.
 *
 * @param {object}   config  - App config
 * @param {object}   auth    - Auth state
 * @param {number[]} ids     - Full list of favorite event IDs
 */
export function pushFavorites (config, auth, ids) {
  requestWithUserAuth(config, auth, (freshAuth) => {
    const api = new AuthorizedApi(
      config,
      (_response) => {
        // No Redux update needed — local state is already authoritative.
      },
      (_error) => {
        // Silently ignore — local favorites remain in effect.
      }
    )
    api.putWithToken('user/favorites', freshAuth?.accessToken, { favorite_event_ids: ids })
  })
}
